<template>
  <Modal @close="$emit('close')">
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          New Package Variation
        </h1>
      </div>
    </template>

    <template #body>
      <div class="h-full w-full min-w-md">
        <form
          class="flex flex-col space-y-4"
          @submit.prevent="saveVariationOrder"
        >
          <div class="font-semibold">
            {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.todayDate') }} {{ new Date().toLocaleDateString() }}
          </div>

          <label>
            {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.dateOfOccurrence') }}

            <AppInput
              v-model="variationOrderForm.form.date_of_occurrence"
              :error="variationOrderForm.getErrors('date_of_occurrence')"
              class="form-input"
              type="date"
              name="date_of_occurrence"
            />
          </label>

          <label>
            {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.description') }}
            <AppTextArea
              v-model="variationOrderForm.form.description"
              name="description"
              class="form-input resize-none"
              :error="variationOrderForm.getErrors('description')"
            />
          </label>

          <label>
            <template v-if="project?.isContractor">
              {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.clientReference') }}
            </template>

            <template v-else>
              {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.contractorReference') }}
            </template>

            <AppInput
              v-model="variationOrderForm.form.client_ref"
              :error="variationOrderForm.getErrors('client_ref')"
              class="form-input"
              type="text"
              name="client_ref"
            />
          </label>

          <label>
            {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.notes') }}
            <textarea
              v-model="variationOrderForm.form.notes"
              name="notes"
              class="form-input resize-none"
            />
          </label>

          <label>
            {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.type') }}

            <AppSelect
              v-model="variationOrderForm.form.type"
              name="type"
              class="form-input capitalize"
              :error="variationOrderForm.getErrors('type')"
              :options="variationTypes"
            />
          </label>

          <template v-if="variationOrderForm.form.type === VariationOrderType.provisional_sum">
            <label>
              {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.budget') }}

              <AppCurrencyInput
                v-model="variationOrderForm.form.budget"
                class="form-input"
                name="budget"
                :error="variationOrderForm.getErrors('budget')"
              />
            </label>

            <label>
              <span class="font-semibold">Linked package trade item</span>

              <AppSelect
                v-model.number="variationOrderForm.form.package_trade_item_id"
                name="package_trade_item_id"
                class="form-input"
                :options="provisionalSumPackageTradeItemOptions"
                :error="variationOrderForm.getErrors('package_trade_item_id')"
                :optional="true"
                empty-text="Select a provisional sum item"
              />
            </label>

            <div
              v-if="linkedTradeItemBudget !== undefined"
              class="flex justify-between"
            >
              <span class="text-gray-600">Contract item budget</span>

              <span class="font-medium">{{ $tcc(linkedTradeItemBudget) }}</span>
            </div>
          </template>

          <template v-if="canUseShortcuts">
            <div class="flex flex-col space-y-4 border-t border-gray-200 pt-4">
              <div class="flex flex-col space-y-2">
                <label class="flex justify-between items-center">
                  {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.noticeReceived') }}

                  <input
                    v-model="variationOrderForm.form.notice_received"
                    type="checkbox"
                    class="form-checkbox"
                    :true-value="1"
                    :false-value="0"
                    name="notice_received"
                  >
                </label>

                <label v-if="variationOrderForm.form.notice_received">
                  {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.noticeDateReceived') }}

                  <AppInput
                    v-model="variationOrderForm.form.notice_date_received"
                    name="notice_date_received"
                    type="date"
                    class="form-input"
                    :error="variationOrderForm.getErrors('notice_date_received')"
                  />
                </label>
              </div>

              <template v-if="variationOrderForm.form.notice_received">
                <div class="flex flex-col space-y-2">
                  <label class="flex justify-between items-center">
                    {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.pricingReceived') }}

                    <input
                      v-model="variationOrderForm.form.pricing_received"
                      type="checkbox"
                      class="form-checkbox"
                      :true-value="1"
                      :false-value="0"
                      name="pricing_received"
                    >
                  </label>

                  <label v-if="variationOrderForm.form.pricing_received">
                    {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.pricingDateReceived') }}

                    <AppInput
                      v-model="variationOrderForm.form.pricing_date_received"
                      name="pricing_date_received"
                      type="date"
                      class="form-input"
                      :error="variationOrderForm.getErrors('pricing_date_received')"
                    />
                  </label>

                  <label v-if="variationOrderForm.form.pricing_received">
                    {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.budget') }}

                    <AppCurrencyInput
                      v-model="variationOrderForm.form.budget"
                      class="form-input"
                      name="budget"
                      :error="variationOrderForm.getErrors('budget')"
                    />
                  </label>
                </div>
              </template>
            </div>
          </template>

          <FileUpload
            v-model="variationOrderForm.form.images"
            multiple
            @change="addFile"
          />

          <span
            v-if="filesUploaded.length"
            class="font-semibold"
          >
            {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.label.attachments') }}
          </span>

          <div
            v-if="filesUploaded.length"
            class="overflow-x-auto max-w-sm flex"
          >
            <UploadedFileDisplay
              v-for="(file, i) in filesUploaded"
              :key="`${file.fileName}-${i}`"
              :file="file"
              :error="variationOrderForm.getErrors(`images.${i}`)"
              @delete="removeFile(i)"
            />
          </div>
        </form>
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close')"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.button.cancel') }}
        </button>

        <button
          type="button"
          class="btn btn--primary-blue"
          :disabled="saveVariationOrderAction.is(States.LOADING)"
          @click="saveVariationOrder"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationSaveModal.button.create') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import Modal from '@/components/Modal.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { australianCurrency, dateForDateInput, falsey } from '@/helpers';
import { VariationOrderType } from '@/models/VariationOrder';
import { usePackagesStore } from '@/store/packages';
import { usePackageTradeItemsStore } from '@/store/packageTradeItems';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, watch } from 'vue';

const props = defineProps<{
  projectId: string;
  packageId: string;
}>();

const emits = defineEmits<{
  (e: 'close'): void;
}>();

const variationOrdersStore = useVariationOrdersStore();
const projectsStore = useProjectsStore();
const packagesStore = usePackagesStore();
const packageTradeItemsStore = usePackageTradeItemsStore();
const userStore = useUserStore();

const project = computed(() => {
  return projectsStore.models.find(props.projectId);
});

const fetchPackageAction = useStoreApiAction(packagesStore.show);

watch(() => props.packageId, (packageId) => {
  if(packageId) {
    fetchPackageAction.request(packageId).catch(console.error);
  }
}, { immediate: true });

const variationOrderForm = useFormData({
  description: '',
  type: 0,
  name: '',
  notes: '',
  images: [],
  date_of_occurrence: dateForDateInput(new Date()),
  budget: undefined,
  client_ref: '',
  package_trade_item_id: undefined as number | undefined,
  notice_received: 0,
  notice_date_received: dateForDateInput(new Date()),
  pricing_received: 0,
  pricing_date_received: dateForDateInput(new Date()),
});

watch(() => variationOrderForm.form.type, (type) => {
  if(type !== VariationOrderType.provisional_sum) {
    variationOrderForm.form.package_trade_item_id = undefined;
  } else {
    variationOrderForm.form.notice_received = 0;
    variationOrderForm.form.pricing_received = 0;
  }
});

watch(() => variationOrderForm.form.notice_received, (received) => {
  if(!received) {
    variationOrderForm.form.pricing_received = 0;
    variationOrderForm.form.budget = undefined;
  }
});

watch(() => variationOrderForm.form.pricing_received, (received) => {
  if(!received) {
    variationOrderForm.form.budget = undefined;
  }
});

const canUseShortcuts = computed(() => {
  return userStore.isRole(['project_admin', 'company_admin'], true, props.projectId) &&
    variationOrderForm.form.type !== VariationOrderType.provisional_sum;
});

const provisionalSumPackageTradeItemOptions = computed(() => {
  if(!props.packageId) {
    return [];
  }

  return packageTradeItemsStore.models
    .where('package_id', parseInt(props.packageId))
    .with('trade_item')
    .get()
    .filter((packageTradeItem) => packageTradeItem.trade_item.is_provisional_sum)
    .map((packageTradeItem) => ({
      value: packageTradeItem.id,
      label: `${packageTradeItem.trade_item.name} (${australianCurrency(packageTradeItem.value)})`,
    }));
});

const linkedTradeItemBudget = computed(() => {
  const packageTradeItemId = variationOrderForm.form.package_trade_item_id;

  if(!packageTradeItemId) {
    return undefined;
  }

  return packageTradeItemsStore.models.find(packageTradeItemId)?.value;
});

const saveVariationOrderAction = useStoreApiAction(variationOrdersStore.storePackageVariationOrder);

const saveVariationOrder = () => {
  variationOrderForm.resetErrors();

  saveVariationOrderAction.request(props.projectId, props.packageId, variationOrderForm.toFormDataObject(true)).then(
    () => {
      Swal.fire('Variation Order Created', '', 'success').then(() => {
        emits('close');
      });
    },
  ).catch((error) => {
    variationOrderForm.setErrors(error.data);
  });
};

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (file, index) => {
  variationOrderForm.form.images.splice(index, 1);
});

const variationTypes = computed(() => {
  const types = [];

  for(const type in VariationOrderType) {
    const value = VariationOrderType[type];

    types.push({ value, label: type.replace('_', ' ') });
  }

  return types;
});
</script>

<style scoped></style>
